import React from "react";

import { DateTime } from "luxon";

import storageKey from "../../../config/storageKey";
import { Dev } from "../../../config/util/consoleUtil";
import useInterval from "../../../config/util/useInterval";

import { Constants } from "../../../config/constants";
import useFirebase from "../../../config/context/firebaseContext";
import {
  BusSchedulesType,
  BusStopListIdsType,
  BusStopTimesType,
  ScheduleRuleInfoType,
  ScheduleRulesType,
} from "../../../types/bus";
import { OnlyChildren } from "../../../types/common";

type BusContextType = {
  currentClock: Date;
  weekDay: WeekDaysType;
  activeStop: BusStopListIdsType | undefined;
  setActiveStop: React.Dispatch<React.SetStateAction<BusStopListIdsType | undefined>>;
  activeRule: ScheduleRuleInfoType | undefined;
  nextBuses: { [busId: string]: NextBusesType };
  nextBusesOnStop: NextBusesOnStopType[];
  busStopTimes: BusStopTimesType;
  busSchedules: BusSchedulesType;
  CUSTOM_NOW: () => DateTime;
};

type WeekDaysType = "0" | "1" | "2" | "3" | "4" | "5" | "6";

type NextBusesType = { busId: string; next?: string; previous?: string; upNext?: string };
type NextBusesOnStopType = { busId: string; next: number };

const BusContext = React.createContext<BusContextType | null>(null);

export function BusProvider({ children }: OnlyChildren) {
  const weekDay = String(new Date().getDay()) as WeekDaysType;
  const { getConfig } = useFirebase();

  const busStopTimes = getConfig("bus_stop_times") as BusStopTimesType;

  const serverBusSchedules = getConfig("bus_schedules") as BusSchedulesType;
  const busSchedules = { ...Constants.busSchedules, ...serverBusSchedules } as BusSchedulesType;

  const serverBusRules = getConfig("bus_rules") as ScheduleRulesType;
  const busRules = { ...Constants.busRules, ...serverBusRules } as ScheduleRulesType;

  const [activeRule, setActiveRule] = React.useState<ScheduleRuleInfoType>();
  const [activeStop, setActiveStop] = React.useState<BusStopListIdsType>();

  const [nextBuses, setNextBuses] = React.useState<{ [busId: string]: NextBusesType }>({});
  const [nextBusesOnStop, setNextBusesOnStop] = React.useState<NextBusesOnStopType[]>([]);

  //

  // const CUSTOM_NOW = (): DateTime => DateTime.now().minus({ hours: 4, minutes: 12 });
  // const CUSTOM_NOW = (): DateTime => DateTime.now().set({ hour: 19 }).set({ minute: 0 });
  const CUSTOM_NOW = (): DateTime => DateTime.now();

  const [currentClock, setCurrentClock] = React.useState(CUSTOM_NOW().toJSDate());

  const handleGetFavoriteStop = () => {
    const storageFavorite = window.localStorage.getItem(storageKey.FAVORITE_STOP);

    if (storageFavorite && Object.keys(Constants.busStopListMap).includes(storageFavorite))
      setActiveStop(storageFavorite as BusStopListIdsType);
  };

  const handleSetupRules = () => {
    const nowTimestamp = new Date().getTime();
    const serverRuleFound = Object.keys(busRules).find(
      (ruleId) =>
        parseInt(String(busRules[ruleId]?.period?.from || "0")) <= nowTimestamp &&
        nowTimestamp <= parseInt(String(busRules[ruleId]?.period?.to || "0"))
    );

    if (serverRuleFound) setActiveRule(busRules[serverRuleFound]);
    else setActiveRule(busRules.default);
  };

  const getNextBusesOnStop = async () => {
    try {
      if (!currentClock || !activeRule) return;

      const newNextBuses = activeRule.days[weekDay].map((busId: string) => {
        let currentHour = `0${currentClock.getHours()}`.slice(-2);
        let currentMinute = `0${currentClock.getMinutes()}`.slice(-2);
        let currentTime = `${currentHour}:${currentMinute}`;

        let isDefined = false;
        let itBus = { busId: busId, next: -1 } as NextBusesOnStopType;

        if (!(busId in busSchedules)) {
          throw new Error("No BusId on the available Schedule List");
        }

        Object.keys(busSchedules[busId].schedule).every((itHour: string) => {
          if (parseInt(currentHour) - 2 <= parseInt(itHour))
            Object.keys(busSchedules[busId].schedule[itHour]).every((itMinute: string) => {
              if (!(String(busId) in busStopTimes[String(activeStop)])) return false;

              let itTime = CUSTOM_NOW()
                .set({ hour: parseInt(itHour) })
                .set({ minute: parseInt(itMinute) });

              if (activeStop) {
                itTime = itTime.plus({
                  minutes: parseInt(busStopTimes[String(activeStop)][busId]) || 0,
                });
              }

              if (itTime.toFormat("HH:mm") > currentTime) {
                const next = itTime.diff(CUSTOM_NOW(), "minutes");
                itBus.next = next.minutes;
                isDefined = true;
                return false;
              }
              return true;
            });

          if (isDefined) return false;
          return true;
        });

        return itBus;
      });

      const result = newNextBuses.filter((item) => item.next > -1);
      setNextBusesOnStop(result);
    } catch (error) {
      console.log(...new Dev("Error getting Next Buses on Stop", error).Error());
    }
  };

  const getNextBuses = () => {
    try {
      if (!currentClock || !activeRule) return;

      const newNextBuses = activeRule.days[weekDay].map((busId: string) => {
        let currentHour = `0${currentClock.getHours()}`.slice(-2);
        let currentMinute = `0${currentClock.getMinutes()}`.slice(-2);
        let currentTime = `${currentHour}:${currentMinute}`;

        let lastIteratedTime = "";
        let isDefined = false;

        let itBus = { busId: busId, next: "", previous: "", upNext: "" } as NextBusesType;

        if (!(busId in busSchedules)) {
          throw new Error("No BusId on the available Schedule List");
        }

        Object.keys(busSchedules[busId].schedule).every((itHour: string, hourI) => {
          if (parseInt(currentHour) - 1 <= parseInt(itHour))
            Object.keys(busSchedules[busId].schedule[itHour]).every((itMinute: string, minuteI) => {
              const itHourValue = `0${itHour}`.slice(-2);
              const itMinuteValue = `0${itMinute}`.slice(-2);
              const itTime = `${itHourValue}:${itMinuteValue}`;

              if (itTime > currentTime) {
                itBus.next = currentTime >= lastIteratedTime ? itTime : lastIteratedTime;
                isDefined = true;
                return false;
              } else {
                lastIteratedTime = `${itHourValue}:${itMinuteValue}`;
                itBus.previous = lastIteratedTime;
              }
              return true;
            });

          if (isDefined) return false;
          return true;
        });

        return itBus;
      });

      const result = {} as { [busId: string]: NextBusesType };
      newNextBuses.map((item) => {
        result[item.busId] = item;
      });
      setNextBuses(result);
    } catch (error) {
      console.log(...new Dev("Error getting Next Buses", error).Error());
    }
  };

  React.useEffect(() => {
    console.log(...new Dev("Mounted BusProvider").Log());

    handleGetFavoriteStop();
    handleSetupRules();
  }, []);

  React.useEffect(() => {
    getNextBuses();

    if (activeStop) {
      getNextBusesOnStop();
    }
  }, [currentClock, activeRule, weekDay]);

  React.useEffect(() => {
    if (activeStop) window.localStorage.setItem(storageKey.FAVORITE_STOP, String(activeStop));

    getNextBusesOnStop();
  }, [activeStop]);

  useInterval(() => {
    setCurrentClock(CUSTOM_NOW().toJSDate());
  }, 5000);

  const value = {
    currentClock,
    activeStop,
    setActiveStop,
    activeRule,
    nextBuses,
    nextBusesOnStop,
    weekDay,
    busStopTimes,
    busSchedules,
    CUSTOM_NOW,
  };

  return <BusContext.Provider value={value}>{children}</BusContext.Provider>;
}

export default function useBus() {
  return React.useContext(BusContext) as BusContextType;
}
